import React from 'react'; 
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { css } from "styled-components";
import CtaButtonRow, { CtaButtonProps } from '../../Elements/CtaButtons/CtaButtonRow';

const verticalDashedLine = css`
    border-left: 2px dashed #333;
    opacity: .2;
    height: 100%;
`;

const stepperContainerField = css`
    text-align: center;
    padding: 1em;
`;

const stepperContainerTitle = css`
    margin-bottom: 10px;
    font-size: 32px;
    line-height: 32px;
    font-family: Vodafone;
    font-weight: 400;

    @media(min-width: 768px) {
        margin-bottom: 20px;
        font-size: 40px;
        line-height: 48px;
    }
`;

const stepperContainerDescription = css`
    margin-bottom: 20px;
    font-size: 18px;
    line-height: 24px;
    font-family: Vodafone;

    @media(min-width: 768px) {
        margin-bottom: 80px;
        font-size: 20px;
        line-height: 28px;
    }
`;

const stepsContiner = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @media(min-width: 768px) {
        align-items: center;
    }
`;

const stepField = css`
    display: grid;
    grid-template-columns: 80px 1fr;
    min-height: unset;

    @media(min-width: 768px) {
        grid-template-columns: 334px 100px 334px;
        min-height: 128px;
    }
`;

const imageAndLineContainer = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
`;

const imageStyle = css`
    width: 32px;
    height: 32px;
    overflow: visible;

    @media(min-width: 768px) {
        width: 48px;
        height: 48px;
    }
`;

const buttonRowWrapper = css`
    margin-top: 0;

    @media(min-width: 768px) {
        margin-top: 40px;
    }
`;

const hideOnMobile = css`
    @media (max-width: 767px) {
        display: none;
    }
`;

const stepTextDivLeftBlock = css`
    display: none;

    @media(min-width: 768px) {
        padding-bottom: 20px;
        display: block;
        text-align: right;
    }
`;

const stepTextDivRightBlock = (isOnLeftSide: boolean) => css`
    padding-bottom: 20px;
    text-align: left;
    display: block;

    @media(min-width: 768px) {
        display: ${isOnLeftSide ? "none" : "block"}
    }
`;

const stepTitle = css`
    padding-bottom: 10px;
    font-family: Vodafone;
    font-weight: 400;
    font-size: 18px;
    line-height: 28px;

    @media(min-width: 768px) {
        font-weight: lighter;
        font-size: 28px;
        line-height: 32px;
    }
`;

const stepDescription = css`
    font-family: Vodafone;
    font-weight: 400;
    font-size: 18px;
    line-height: 26px;
`;

interface StepImage {
    gatsbyImageData: IGatsbyImageData;
}

interface SectionCustomStepperContent {
    name: string;
    title: string;
    stepText: string;
    stepImage: StepImage;
}

interface SectionCustomStepperProps {
    title: string;
    text: string;
    content: SectionCustomStepperContent[];
    buttons: CtaButtonProps[];
    buttonRowAlignment: "left" | "centre" | "right";
}

const SectionCustomStepper = ({ title, text, content, buttons, buttonRowAlignment }: SectionCustomStepperProps) => {
    return (
        <div css={stepperContainerField}>
            <div css={stepperContainerTitle}>
                {title}
            </div>
            <div css={stepperContainerDescription}>
                {text}
            </div>
            <div css={stepsContiner}>
                {content.map((item, index) => {
                    const isOnLeftSide = index % 2 === 1;
                    return (
                        <div key={index} css={stepField}>
                            {isOnLeftSide ?
                                <div css={stepTextDivLeftBlock}>
                                    <div css={stepTitle}>{item.title}</div>
                                    <div css={stepDescription}>{item.stepText}</div>
                                </div>
                                :
                                <div css={hideOnMobile} />
                            }
                            <div>
                                <div css={imageAndLineContainer}>
                                    <GatsbyImage
                                        image={getImage(item.stepImage.gatsbyImageData) as IGatsbyImageData}
                                        css={imageStyle}
                                        alt={"Step " + (index + 1)}
                                    />
                                    {index + 1 < content.length &&
                                        <div css={verticalDashedLine} />
                                    }
                                </div>
                            </div>
                            <div css={stepTextDivRightBlock(isOnLeftSide)}>
                                <div css={stepTitle}>{item.title}</div>
                                <div css={stepDescription}>{item.stepText}</div>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div css={buttonRowWrapper}>
                <CtaButtonRow
                    buttonRowAlignment={buttonRowAlignment}
                    buttons={buttons}
                />
            </div>
        </div>
    );
};

export default SectionCustomStepper;
